import { CarFront, CircleHelp, CirclePlus, House, UserRound } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { cn } from "../../lib/format";
import { useT } from "../../i18n";

/**
 * Bottom tab bar of the public site on phones and tablets (hidden from lg up,
 * where the header shows the full nav). Frosted glass floating over the page;
 * index.css pads the page bottom so nothing stays hidden behind it.
 * Rendered in <body>: inside the header (backdrop-blur) "fixed" would stick to the header.
 */
export default function MobileTabBar({ account }: { account: { href: string; label: string } }) {
  const t = useT();
  const { pathname } = useLocation();

  const tabs: { href: string; label: string; icon: LucideIcon; match: (p: string) => boolean }[] = [
    { href: "/", label: t.header.tabs.home, icon: House, match: (p) => p === "/" },
    { href: "/vehicles", label: t.header.tabs.findCar, icon: CarFront, match: (p) => p.startsWith("/vehicles") },
    { href: "/partners", label: t.header.tabs.listCar, icon: CirclePlus, match: (p) => p.startsWith("/partners") },
    { href: "/how-it-works", label: t.header.tabs.howItWorks, icon: CircleHelp, match: (p) => p.startsWith("/how-it-works") },
    { href: account.href, label: account.label, icon: UserRound, match: (p) => p === account.href || p === "/signup" },
  ];

  return createPortal(
    <nav aria-label={t.header.tabs.aria} className="mobile-tabbar fixed inset-x-3 bottom-[max(12px,env(safe-area-inset-bottom))] z-50 lg:hidden">
      <ul className="m-0 grid list-none grid-cols-5 gap-1 rounded-2xl border border-white/60 bg-white/55 p-1.5 shadow-[0_8px_32px_rgba(23,26,32,0.16),inset_0_1px_0_rgba(255,255,255,0.7)] backdrop-blur-xl backdrop-saturate-150">
        {tabs.map(({ href, label, icon: Icon, match }) => {
          const active = match(pathname);
          return (
            <li key={label}>
              <a
                href={href}
                aria-current={active ? "page" : undefined}
                className={cn(
                  "flex flex-col items-center gap-0.5 rounded-xl px-0.5 py-1.5 text-[10px] font-medium leading-tight tracking-tight no-underline transition-colors",
                  active ? "bg-white/80 text-brand shadow-sm" : "text-ink-soft hover:bg-white/40",
                )}
              >
                <Icon size={21} strokeWidth={active ? 2.2 : 1.8} aria-hidden="true" />
                <span className="max-w-full truncate">{label}</span>
              </a>
            </li>
          );
        })}
      </ul>
    </nav>,
    document.body,
  );
}
